// Best-performing Meta creatives — the lowest-CPL ad creatives across the
// book for the selected range, from STORED per-ad rows + the Phase-3c
// thumbnails (no new pull). Volume-guarded via rankBestCreatives; a missing
// thumbnail renders a placeholder, never a broken card.
//
// ⚠️⚠️ ONE RENDERER, SHARED BY THE AM BOARD AND THE META BOARD, and that is
// the point rather than a convenience: every image here goes through the
// SIGNED PROXY (`proxiedCreativeImage`, applied server-side where the row is
// built). ~81% of stored creative URLs are `www.facebook.com/ads/image/?d=…`,
// and an `/ads/` segment on a facebook.com path matches standard ad-blocker
// filter lists — the request never leaves the browser and a client-running
// uBlock sees a wall of "No preview". That has drifted back to a raw
// `thumbnail_url` THREE times. A second renderer would be a fourth chance.
//
// ⚠️⚠️ EACH CREATIVE RENDERS AT ITS OWN INTRINSIC RATIO (2026-09-17, round 3),
// inside a COMMON WIDTH — not inside one aspect box. The card was `aspect-[4/5]`
// + `object-cover`, which is right for the 4:5 majority and ZOOMS A SQUARE
// ASSET to fill, cropping the edges off it. 4:5 stays 4:5, square stays square,
// and nothing is cropped: the frame follows the image rather than the image
// being made to fit the frame.
//
// ⚠️ TOP-ALIGNED WITH A MAX HEIGHT, because a grid of varying heights needs
// both: `items-start` so a short card does not stretch to its neighbour, and a
// ceiling so one unusually tall asset cannot dominate a row. The ceiling is the
// ONE place `object-cover` survives — past it there is no way to show the whole
// image at a readable width anyway.
//
// ⚠️⚠️ "NATURAL RATIO" IS NOT "NATURAL SIZE", and the difference is why the
// width is fixed. Roughly 15% of stored URLs are the
// `scontent-*.fbcdn.net/v/t15…` family with `p64x64` baked in — genuinely 64px
// images. They are soft at card width WHATEVER the ratio, and that is the
// accepted outcome: rendering them at their true 64px would give the gallery
// random tiny tiles, which is worse than soft ones. So `w-full` is deliberate
// and must not become "size to the asset".

import { AccountLinkIcons } from '../../components/ui/AccountLinkIcons';
import type { AccountLinkKey } from '../../lib/accountLinks';
import { Link } from 'react-router-dom';
import { ImageOff, Play } from 'lucide-react';
import { cn } from '../../lib/utils';
import { rankBestCreatives, rankSpendingNoLeadCreatives } from '../../lib/advertisingHome/creativesRanking';
import { CREATIVE_MIN_LEADS, CREATIVE_MIN_SPEND } from '../../lib/advertisingHome/guards';
import type { BrandWindowRow, HomeCreative } from '../../types/advertisingHome';
import { BrandAverages } from '../../lib/advertisingHome/noLeads';
import type { FxRateSnapshot } from '../../types/fxRates';
import { PanelTitle } from '../reporting/PanelTitle';
import { creativeHref } from './deepLinks';
import { formatLeads, homeSingleCurrencyCpl } from './homeMoney';

/**
 * The ceiling on a single card. An extreme portrait (a 9:16 story asset among
 * 4:5 feed ones) would otherwise set the height of its whole row; past this the
 * image crops, which is the lesser harm at a size nobody can read anyway.
 */
const MAX_THUMB_HEIGHT = 'max-h-[22rem]';

function Thumb({ creative }: { creative: HomeCreative }): React.JSX.Element {
  if (!creative.thumbnailUrl) {
    // ⚠️ THE PLACEHOLDER KEEPS A FIXED RATIO, and that is not an inconsistency:
    // there is no image to take a ratio FROM, and a tile that collapsed to its
    // icon would read as a broken card rather than a missing preview.
    return (
      <div className="flex aspect-[4/5] w-full items-center justify-center rounded bg-muted text-muted-foreground">
        <ImageOff className="h-6 w-6" aria-hidden />
      </div>
    );
  }
  return (
    <div className="relative w-full overflow-hidden rounded bg-muted">
      <img
        src={creative.thumbnailUrl}
        alt={creative.adName ?? 'Ad creative'}
        // ⚠️ `h-auto` IS THE WHOLE FIX — the card sizes to the image's own
        // ratio. `object-cover` applies ONLY at the ceiling, where the height is
        // constrained and something has to give.
        className={cn('w-full object-cover', MAX_THUMB_HEIGHT)}
        style={{ height: 'auto' }}
        // The Meta CDN URLs are short-lived — degrade to the placeholder tile
        // rather than a broken-image frame.
        onError={(e) => {
          const el = e.currentTarget;
          el.style.display = 'none';
          const parent = el.parentElement;
          parent?.classList.add('flex', 'aspect-[4/5]', 'items-center', 'justify-center');
        }}
      />
      {creative.videoUrl && (
        <span className="absolute bottom-1 right-1 rounded bg-black/60 p-1 text-white">
          <Play className="h-3 w-3" aria-hidden />
        </span>
      )}
    </div>
  );
}

export function CreativesCard({
  rows,
  display,
  snapshot,
  linkKeys,
  noLeadRows = [],
  brandWindows = [],
  brandOfAccount = () => null,
}: {
  /** Ads that spent with zero leads (shown explicitly — never dropped). */
  noLeadRows?: HomeCreative[];
  brandWindows?: BrandWindowRow[];
  brandOfAccount?: (accountId: string) => string | null;
  /** When set, the account name carries ONLY these links' icons (outside the card link). */
  linkKeys?: AccountLinkKey[];
  rows: HomeCreative[];
  display: string;
  snapshot: FxRateSnapshot | null | undefined;
}): React.JSX.Element {
  const ranked = rankBestCreatives(rows);
  const noLeads = rankSpendingNoLeadCreatives(
    noLeadRows,
    brandOfAccount,
    new BrandAverages(brandWindows),
  );
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <PanelTitle>Best-performing Meta creatives</PanelTitle>
        <span className="text-[10px] text-muted-foreground">
          lowest CPL · min {CREATIVE_MIN_LEADS} leads + ${CREATIVE_MIN_SPEND} spend
        </span>
      </div>
      {ranked.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No ads with qualifying volume in this range.
        </p>
      ) : (
        // Four rows at the six-across width, scrolling internally past that.
        <div className="scroll-subtle grid max-h-[34rem] grid-cols-2 items-start gap-3 overflow-y-auto pr-1 sm:grid-cols-3 lg:grid-cols-6">
          {ranked.map((c) => {
            const top = (
              <>
                <Thumb creative={c} />
                <span className="mt-1 block truncate text-xs font-medium group-hover:underline">
                  {c.adName ?? c.adId}
                </span>
              </>
            );
            const rest = (
              <>
                {c.campaignName && (
                  <span
                    className="block truncate text-[11px] text-muted-foreground"
                    title={c.campaignName}
                  >
                    {c.campaignName}
                  </span>
                )}
                <span className="block text-[11px] tabular-nums text-muted-foreground">
                  {/* ⚠️ THE SINGLE-CURRENCY helper, never the blended one: a
                      creative is one ad account and therefore one currency, and
                      the blended helper has no single-number form in native mode
                      — which is why every card here read "— CPL" beside a real
                      lead count. See homeSingleCurrencyCpl. */}
                  {homeSingleCurrencyCpl(c.spend, c.currency, c.leads, display, snapshot)} CPL ·{' '}
                  {formatLeads(c.leads)} leads
                </span>
              </>
            );
            const accountName = c.accountName ?? c.accountId;
            const linkProps = {
              to: creativeHref(c.accountId),
              className: 'group block min-w-0',
            };
            // With linkKeys the account line carries link ICONS, which are
            // anchors — so it sits BETWEEN two card Links rather than inside
            // one (an anchor inside an anchor is invalid).
            return linkKeys ? (
              <div key={c.adId} className="min-w-0">
                <Link {...linkProps} title={c.adName ?? c.adId}>
                  {top}
                </Link>
                <span className="flex min-w-0 items-center gap-1">
                  <span className="truncate text-[11px] text-muted-foreground" title={accountName}>
                    {accountName}
                  </span>
                  <AccountLinkIcons accountId={c.accountId} className="shrink-0" only={linkKeys} />
                </span>
                <Link {...linkProps}>{rest}</Link>
              </div>
            ) : (
              <div key={c.adId} className="min-w-0">
                <Link {...linkProps} title={c.adName ?? c.adId}>
                  {top}
                  <span className="block truncate text-[11px] text-muted-foreground">{accountName}</span>
                  {rest}
                </Link>
              </div>
            );
          })}
        </div>
      )}
      {noLeads.length > 0 && (
        <div className="mt-3 border-t border-border pt-3" data-testid="creatives-no-leads">
          <p className="mb-2 text-xs font-medium text-red-700">
            Spending, no leads ({noLeads.length})
          </p>
          <ul className="scroll-subtle max-h-48 space-y-1 overflow-y-auto pr-1 text-xs">
            {noLeads.map((c) => (
              <li key={c.adId} className="flex items-baseline justify-between gap-2">
                <Link to={creativeHref(c.accountId)} className="min-w-0 truncate hover:underline">
                  {c.adName ?? c.adId}{' '}
                  <span className="text-muted-foreground">· {c.accountName ?? c.accountId}</span>
                </Link>
                <span className="shrink-0 tabular-nums text-red-700">
                  No leads ·{' '}
                  {homeSingleCurrencyCpl(c.spend, c.currency, 1, display, snapshot)} spent
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
